<template>
  <div>
    <validated-input
      class="mar-b-xs"
      label="Modifier Restrictions"
      name="modifierRestriction"
      :disabled="readOnly"
    />

    <b-message
      type="is-info"
      class="has-shadow is-compact"
      title="IMPORTANT NOTES"
      has-icon
      closable
    >
      <ol>
        <li>
          <b>Add all {{ isCategorySearch ? 'categories' : 'items' }} first.</b> Adding or removing {{ isCategorySearch ? 'a category' : 'an item' }} will reset all modifier restrictions.
        </li>
        <li>
          {{ isCategorySearch ? 'A' : 'An' }} {{ isCategorySearch ? 'category' : 'item' }} on an order will qualify if it is one of the configured {{ isCategorySearch ? 'categories' : 'items' }} above and has <em>at least one</em> modifier found in the selected modifiers section below. For instance, if you chose items <b>"Item 1"</b> and <b>"Item 2"</b> and you've selected the modifiers <b>"Modifier A"</b>, <b>"Modifier B"</b>, and <b>"Modifier C"</b>, then the qualifying combinations are <b>"Item 1 Modifier A"</b> OR <b>"Item 1 Modifier B"</b> OR <b>"Item 1 Modifier C"</b> OR <b>"Item 2 Modifier A"</b> OR <b>"Item 2 Modifier B"</b> OR <b>"Item 2 Modifier C"</b>.
        </li>
      </ol>
    </b-message>

    <div class="modifier-restrictions-container">
      <div class="is-grid gap-xs has-border-bottom has-border-grey-lighter pad row">
        <label class="label is-flex mar-none">Name</label>
        <label class="label is-flex justify-center mar-none">Selected Modifiers</label>
      </div>
      <div
        v-if="!selectedResources.length"
        class="pad-xl text-align-center"
      >
        Add <b>{{ isCategorySearch ? 'Categories' : 'Items' }}</b> to manage their modifier restrictions.
      </div>
      <modifier-restriction-row
        v-for="(resource, resourceIndex) in selectedResources"
        :key="`${isCategorySearch ? 'category' : 'item'}-${resourceIndex}`"
        :resource="resource"
        :all-resources-selected-modifier-ids="allResourcesSelectedModifierIds"
        :is-category-search="isCategorySearch"
        :read-only="readOnly"
        @update-resource-restricted-modifiers="handleResourceModifierChange"
      />
      <div v-if="missingModifierIds.length && !readOnly" class="row col-span-3 mar-t-sm">
        <b-message
          type="is-danger"
          class="has-shadow is-compact"
        >
          There are some modifiers associated with this item group that no longer exist.
          You must remove them to save any changes. Would you like to remove them?
          <b-button
            size="is-small"
            type="is-danger"
            class="pad-y-xxs pad-x-xs"
            @click="removeMissingModifiers"
          >
            <b>Yes</b>
          </b-button>
        </b-message>
      </div>
    </div>
  </div>
</template>

<script>
  import merchantMixin from '@/mixins/merchant';

  import ModifierRestrictionRow from './modifier-restriction-row.vue';

  import Modifier from '@/store/classes/Modifier';

  export default {
    name: 'ModifierRestrictionsSearch',

    components: { ModifierRestrictionRow },

    mixins: [merchantMixin],


    props: {
      selectedResources: {
        type: Array,
        default: () => []
      },

      requiredModifications: {
        type: Array,
        default: () => []
      },

      readOnly: {
        type: Boolean,
        required: true
      },

      isCategorySearch: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        loadingModifiers: false
      };
    },

    computed: {
      allResourcesSelectedModifierIds() {
        return this.requiredModifications?.[0]?.qualifyingModifierMenuItemIds || [];
      },

      missingModifierIds() {
        const allModifierIds = [...new Set(Modifier.all().map(modifier => modifier.menuItemId))];
        return this.allResourcesSelectedModifierIds.filter(id => !allModifierIds.includes(id));
      }
    },

    methods: {
      handleResourceModifierChange({ addedModifierIds, removedModifierIds }) {
        const qualifyingModifierMenuItemIds = [
          ...new Set([...addedModifierIds, ...this.allResourcesSelectedModifierIds])
        ].filter(modifierId => !removedModifierIds.includes(modifierId));

        this.$emit('update-required-modifications', [{ qualifyingModifierMenuItemIds }]);
      },

      removeMissingModifiers() {
        const updatedModifiers = this.allResourcesSelectedModifierIds.filter(modifierId => !this.missingModifierIds.includes(modifierId));
        this.$emit('update-required-modifications', [{ qualifyingModifierMenuItemIds: updatedModifiers }]);
      }
    }
  };
</script>

<style lang="sass" scoped>
  .modifier-restrictions-container
    background-color: $white
    >*:nth-child(even)
      background-color: $light

  .row
    grid-template-columns: 150px 1fr 150px

  .tag
    display: flex
    align-items: center
    justify-content: space-between
    padding: 0.5rem
    max-width: 240px

  .tag-max
    max-width: 180px
</style>
